import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ArrowRightLeft,
  CheckCircle,
  Clock,
  Cpu,
  Database,
  FileCheck,
  MessageCircle,
  Send,
  ShieldCheck,
  Workflow,
  X,
  XCircle
} from 'lucide-react';

import ContactForm from '../ContactForm';
import LanguageSwitcher from '../components/LanguageSwitcher';

const PageHeader = ({ onOpenPopup, t }) => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
      <a href="/" className="flex items-center">
        <img
          src="https://imgur.com/nYWIvWJ.png"
          alt="Nextcoders Logo"
          className="h-8"
          onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders"; }}
        />
      </a>
      <div className="flex items-center gap-4 md:gap-8">
        <LanguageSwitcher variant="compact" />
        <button
          onClick={onOpenPopup}
          className="bg-[#141414] text-white px-4 md:px-6 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-semibold"
        >
          {t('api_tool.cta_demo')}
        </button>
      </div>
    </div>
  </header>
);

const ToolCard = ({ icon: Icon, title, description, colorClass, metrics }) => (
  <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300 flex flex-col h-full">
    <div className="flex items-start gap-4">
      <div className={`p-3 rounded-xl ${colorClass.bg} ${colorClass.text} flex-shrink-0`}>
        <Icon size={26} />
      </div>
      <div className="min-w-0">
        <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
    <div className="mt-6 space-y-3">
      {metrics.map((metric) => (
        <div key={metric.label}>
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span className="font-medium">{metric.label}</span>
            <span>{metric.value}</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full ${metric.barClass}`} style={{ width: metric.width }} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ScenarioCard = ({ number, icon: Icon, title, description }) => (
  <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300 h-full">
    <div className="flex items-start gap-4 mb-4">
      <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0 relative">
        <Icon size={22} />
        <div className="absolute -top-2 -right-2 w-7 h-7 bg-[#141414] text-white text-xs rounded-full flex items-center justify-center border-2 border-white">
          {number}
        </div>
      </div>
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-1">{title}</h3>
        <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  </div>
);

const ComparisonRow = ({ label, withTool, withoutTool }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center py-4 border-t border-gray-100">
    <div className="text-gray-800 font-semibold">{label}</div>
    <div className="bg-green-50 border border-green-100 rounded-xl p-4 flex items-center gap-3">
      <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
      <span className="text-gray-700 text-sm">{withTool}</span>
    </div>
    <div className="bg-red-50 border border-red-100 rounded-xl p-4 flex items-center gap-3">
      <XCircle className="text-red-600 flex-shrink-0" size={20} />
      <span className="text-gray-700 text-sm">{withoutTool}</span>
    </div>
  </div>
);

const ApiToolPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const toolLabels = t('api_tool.tools', { returnObjects: true });
  const advantages = t('api_tool.advantages', { returnObjects: true });
  const scenarios = t('api_tool.scenarios', { returnObjects: true });
  const workflowRows = t('api_tool.workflow_rows', { returnObjects: true });
  const workflowHeaders = t('api_tool.workflow_headers', { returnObjects: true });
  const architectureLabels = t('api_tool.architecture_labels', { returnObjects: true });
  const architectureSubtitles = t('api_tool.architecture_subtitles', { returnObjects: true });

  const toolMetrics = useMemo(() => ([
    {
      icon: Cpu,
      title: toolLabels?.[0]?.title ?? 'Consulta con IA',
      description: toolLabels?.[0]?.description ?? 'Integra Inteligencia Artificial en tus flujos para procesar datos, analizar sentimientos o clasificar información en tiempo real.',
      colorClass: { bg: 'bg-indigo-50', text: 'text-indigo-600' },
      metrics: [
        { label: toolLabels?.[0]?.metrics?.[0]?.label ?? 'Clasificación', value: toolLabels?.[0]?.metrics?.[0]?.val ?? 'en tiempo real', width: '85%', barClass: 'bg-indigo-500' },
        { label: toolLabels?.[0]?.metrics?.[1]?.label ?? 'Automatización', value: toolLabels?.[0]?.metrics?.[1]?.val ?? 'alta', width: '80%', barClass: 'bg-indigo-400' },
        { label: toolLabels?.[0]?.metrics?.[2]?.label ?? 'Consistencia', value: toolLabels?.[0]?.metrics?.[2]?.val ?? 'estable', width: '75%', barClass: 'bg-indigo-300' }
      ]
    },
    {
      icon: FileCheck,
      title: toolLabels?.[1]?.title ?? 'Consulta RUC',
      description: toolLabels?.[1]?.description ?? 'Valida y extrae información oficial de contribuyentes de forma automática, ideal para facturación o registro de clientes.',
      colorClass: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
      metrics: [
        { label: toolLabels?.[1]?.metrics?.[0]?.label ?? 'Validación', value: toolLabels?.[1]?.metrics?.[0]?.val ?? 'automática', width: '90%', barClass: 'bg-emerald-500' },
        { label: toolLabels?.[1]?.metrics?.[1]?.label ?? 'Calidad de datos', value: toolLabels?.[1]?.metrics?.[1]?.val ?? 'alta', width: '85%', barClass: 'bg-emerald-400' },
        { label: toolLabels?.[1]?.metrics?.[2]?.label ?? 'Riesgo', value: toolLabels?.[1]?.metrics?.[2]?.val ?? 'bajo', width: '70%', barClass: 'bg-emerald-300' }
      ]
    },
    {
      icon: Clock,
      title: toolLabels?.[2]?.title ?? 'Tiempo de Espera (Delay)',
      description: toolLabels?.[2]?.description ?? 'Introduce pausas precisas en segundos para dar tiempo a procesos externos antes de continuar el flujo en Bitrix24.',
      colorClass: { bg: 'bg-amber-50', text: 'text-amber-600' },
      metrics: [
        { label: toolLabels?.[2]?.metrics?.[0]?.label ?? 'Control', value: toolLabels?.[2]?.metrics?.[0]?.val ?? 'preciso', width: '88%', barClass: 'bg-amber-500' },
        { label: toolLabels?.[2]?.metrics?.[1]?.label ?? 'Orquestación', value: toolLabels?.[2]?.metrics?.[1]?.val ?? 'mejorada', width: '78%', barClass: 'bg-amber-400' },
        { label: toolLabels?.[2]?.metrics?.[2]?.label ?? 'Errores', value: toolLabels?.[2]?.metrics?.[2]?.val ?? 'menos reintentos', width: '72%', barClass: 'bg-amber-300' }
      ]
    }
  ]), [toolLabels]);

  const openPopup = () => {
    setIsPopupOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    document.body.style.overflow = 'auto';
  };

  return (
    <div className="relative min-h-screen bg-transparent font-inter">
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) closePopup(); }}>
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative transform transition-all duration-300 scale-100">
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition"
              aria-label="Cerrar popup"
            >
              <X size={24} />
            </button>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#141414] mb-3">{t('api_tool.popup.title')}</h3>
              <p className="text-gray-600 mb-6">{t('api_tool.popup.desc')}</p>
              <ContactForm
                formData="inline/4/10oz6w"
                formId="4"
                scriptSrc="https://cdn.bitrix24.es/b24284919/crm/form/loader_4.js"
              />
            </div>
          </div>
        </div>
      )}

      <PageHeader onOpenPopup={openPopup} t={t} />

      <main className="pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </button>

          <div className="text-center mb-20">
            <div className="inline-block bg-blue-50 text-blue-600 p-3 rounded-full mb-4">
              <Workflow size={32} />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#141414] mb-6">{t('api_tool.title')}</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8 font-medium">
              {t('api_tool.subtitle')}
            </p>
            <p className="text-gray-500 max-w-4xl mx-auto leading-relaxed">
              {t('api_tool.description')}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
              <button
                onClick={openPopup}
                className="bg-[#141414] text-white hover:bg-gray-900 font-bold py-3 px-8 rounded-full transition"
              >
                {t('api_tool.cta_activate')}
              </button>
              <a
                href="#escenarios"
                className="bg-white border border-gray-200 text-[#141414] hover:bg-gray-50 font-bold py-3 px-8 rounded-full transition"
              >
                {t('api_tool.cta_scenarios')}
              </a>
            </div>
          </div>

          <section className="mb-24">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-6">{t('api_tool.tools_title')}</h2>
            <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">
              {t('api_tool.tools_subtitle')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {toolMetrics.map((tool) => (
                <ToolCard
                  key={tool.title}
                  icon={tool.icon}
                  title={tool.title}
                  description={tool.description}
                  colorClass={tool.colorClass}
                  metrics={tool.metrics}
                />
              ))}
            </div>
            <div className="mt-6 max-w-3xl mx-auto rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900 text-center">
              {t('api_tool.ruc_note')}
            </div>
          </section>

          <section className="mb-24 bg-gray-50 rounded-3xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-6">{t('api_tool.advantages_title')}</h2>
            <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">
              {t('api_tool.advantages_subtitle')}
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
              <div className="bg-white border border-gray-200 rounded-2xl p-8 h-full flex flex-col">
                <ul className="space-y-5">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={22} />
                    <div>
                      <div className="font-bold text-gray-900">{advantages?.[0]?.title}</div>
                      <div className="text-gray-600 text-sm">{advantages?.[0]?.desc}</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={22} />
                    <div>
                      <div className="font-bold text-gray-900">{advantages?.[1]?.title}</div>
                      <div className="text-gray-600 text-sm">{advantages?.[1]?.desc}</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={22} />
                    <div>
                      <div className="font-bold text-gray-900">{advantages?.[2]?.title}</div>
                      <div className="text-gray-600 text-sm">{advantages?.[2]?.desc}</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={22} />
                    <div>
                      <div className="font-bold text-gray-900">{advantages?.[3]?.title}</div>
                      <div className="text-gray-600 text-sm">{advantages?.[3]?.desc}</div>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 rounded-2xl p-8 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-6">
                  <ShieldCheck className="text-blue-600" size={26} />
                  <h3 className="text-xl font-bold text-gray-900">{t('api_tool.architecture_title')}</h3>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div className="flex flex-col sm:flex-row items-stretch gap-4">
                    <div className="flex-1 border border-gray-200 rounded-xl p-4 bg-gray-50">
                      <div className="flex items-center gap-3">
                        <Workflow className="text-gray-700" size={22} />
                        <div>
                          <div className="font-bold text-gray-900">{architectureLabels?.[0]}</div>
                          <div className="text-xs text-gray-500">{architectureSubtitles?.[0]}</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-center text-gray-400 font-bold">
                      <ArrowRightLeft size={22} />
                    </div>
                    <div className="flex-1 border border-blue-200 rounded-xl p-4 bg-blue-50">
                      <div className="flex items-center gap-3">
                        <Cpu className="text-blue-700" size={22} />
                        <div>
                          <div className="font-bold text-gray-900">{architectureLabels?.[1]}</div>
                          <div className="text-xs text-gray-600">{architectureSubtitles?.[1]}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center text-gray-400">
                    <Send size={20} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <Database className="text-indigo-600" size={20} />
                        <div>
                          <div className="font-semibold text-gray-900">{architectureLabels?.[2]}</div>
                          <div className="text-xs text-gray-500">{architectureSubtitles?.[2]}</div>
                        </div>
                      </div>
                    </div>
                    <div className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <FileCheck className="text-emerald-600" size={20} />
                        <div>
                          <div className="font-semibold text-gray-900">{architectureLabels?.[3]}</div>
                          <div className="text-xs text-gray-500">{architectureSubtitles?.[3]}</div>
                        </div>
                      </div>
                    </div>
                    <div className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <Cpu className="text-amber-600" size={20} />
                        <div>
                          <div className="font-semibold text-gray-900">{architectureLabels?.[4]}</div>
                          <div className="text-xs text-gray-500">{architectureSubtitles?.[4]}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 text-xs text-gray-500">
                  {t('api_tool.architecture_footer')}
                </div>
              </div>
            </div>
          </section>

          <section id="escenarios" className="mb-24">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-6">{t('api_tool.scenarios_title')}</h2>
            <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">
              {t('api_tool.scenarios_subtitle')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <ScenarioCard
                number="1"
                icon={FileCheck}
                title={scenarios?.[0]?.title}
                description={scenarios?.[0]?.desc}
              />
              <ScenarioCard
                number="2"
                icon={MessageCircle}
                title={scenarios?.[1]?.title}
                description={scenarios?.[1]?.desc}
              />
              <ScenarioCard
                number="3"
                icon={Send}
                title={scenarios?.[2]?.title}
                description={scenarios?.[2]?.desc}
              />
              <ScenarioCard
                number="4"
                icon={Database}
                title={scenarios?.[3]?.title}
                description={scenarios?.[3]?.desc}
              />
            </div>
          </section>

          <section className="mb-24">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-6">{t('api_tool.workflow_title')}</h2>
            <p className="text-center text-gray-600 max-w-3xl mx-auto mb-10">
              {t('api_tool.workflow_subtitle')}
            </p>
            <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-10">
              <div className="hidden md:grid md:grid-cols-3 gap-4 pb-4 border-b border-gray-100 text-sm font-bold text-gray-600">
                <div>{workflowHeaders?.[0]}</div>
                <div className="text-green-700">{workflowHeaders?.[1]}</div>
                <div className="text-red-700">{workflowHeaders?.[2]}</div>
              </div>
              <ComparisonRow
                label={workflowRows?.[0]?.label}
                withTool={workflowRows?.[0]?.with}
                withoutTool={workflowRows?.[0]?.without}
              />
              <ComparisonRow
                label={workflowRows?.[1]?.label}
                withTool={workflowRows?.[1]?.with}
                withoutTool={workflowRows?.[1]?.without}
              />
              <ComparisonRow
                label={workflowRows?.[2]?.label}
                withTool={workflowRows?.[2]?.with}
                withoutTool={workflowRows?.[2]?.without}
              />
              <ComparisonRow
                label={workflowRows?.[3]?.label}
                withTool={workflowRows?.[3]?.with}
                withoutTool={workflowRows?.[3]?.without}
              />
            </div>
          </section>

          <section className="text-center bg-[#141414] rounded-3xl p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-blue-900/20 to-indigo-900/20 z-0" />
            <div className="relative z-10">
              <ArrowRightLeft size={48} className="mx-auto mb-6 text-blue-400" />
              <h2 className="text-3xl font-bold mb-6">{t('api_tool.final_title')}</h2>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto mb-8">
                {t('api_tool.final_desc')}
              </p>
              <div className="flex flex-col md:flex-row justify-center gap-4">
                <button
                  onClick={openPopup}
                  className="bg-white text-[#141414] hover:bg-gray-100 font-bold py-3 px-8 rounded-full transition"
                >
                  {t('api_tool.final_cta_activate')}
                </button>
                <a
                  href="/soporte"
                  className="bg-transparent border border-white text-white hover:bg-white/10 font-bold py-3 px-8 rounded-full transition"
                >
                  {t('api_tool.final_cta_support')}
                </a>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default ApiToolPage;

